// src/pages/Onboarding.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { AuthContext } from '../context/AuthContext';

const FIXED_TOPICS = [
  {
    id: 'english',
    title: 'English Learning',
    icon: '🇬🇧',
    description: 'Structured English courses from beginner to advanced.',
  },
  {
    id: 'advanced-english',
    title: 'Advanced English',
    icon: '📖',
    description: 'Advanced English content that forms the basis of our programs.',
  },
  {
    id: 'sustainability',
    title: 'Sustainability Programs',
    icon: '🌱',
    description: 'Programs focused on sustainable living and practices.',
  },
  {
    id: 'social-entrepreneurship',
    title: 'Social Entrepreneurship',
    icon: '🤝',
    description: 'Courses on building ventures with social impact.',
  },
];

export default function Onboarding() {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1 = pick topic, 2 = pick courses
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicCatalogs, setTopicCatalogs] = useState([]);
  const [selectedCatalogs, setSelectedCatalogs] = useState([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load catalogs for the selected topic
  useEffect(() => {
    if (!selectedTopic) return;

    const loadCatalogs = async () => {
      setLoadingCatalogs(true);
      setTopicCatalogs([]);
      setSelectedCatalogs([]);
      try {
        const topicDoc = await getDoc(doc(db, 'topics', selectedTopic.id));
        if (!topicDoc.exists()) {
          setTopicCatalogs([]);
          setLoadingCatalogs(false);
          return;
        }
        const catalogIds = topicDoc.data().catalogIds || [];
        if (catalogIds.length === 0) {
          setTopicCatalogs([]);
          setLoadingCatalogs(false);
          return;
        }
        // Fetch each catalog doc
        const catalogDocs = await Promise.all(
          catalogIds.map(id => getDoc(doc(db, 'catalogs', id)))
        );
        const catalogs = catalogDocs
          .filter(d => d.exists())
          .map(d => ({ id: d.id, ...d.data() }));
        setTopicCatalogs(catalogs);
      } catch (e) {
        console.error('Error loading catalogs:', e);
        setError('Could not load courses. Please try again.');
      } finally {
        setLoadingCatalogs(false);
      }
    };

    loadCatalogs();
  }, [selectedTopic]);

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    setStep(2);
    setError('');
  };

  const toggleCatalog = (catalogId) => {
    setSelectedCatalogs(prev =>
      prev.includes(catalogId)
        ? prev.filter(id => id !== catalogId)
        : [...prev, catalogId]
    );
  };

  const handleConfirm = async () => {
    if (selectedCatalogs.length === 0) {
      setError('Please select at least one course to continue.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const uid = currentUser.uid;

      // 1. Write learner profile — merge so existing data isn't overwritten
      await setDoc(doc(db, 'learnerProfiles', uid), {
        userId: uid,
        topicId: selectedTopic.id,
        topicTitle: selectedTopic.title,
        enrolledCatalogIds: selectedCatalogs,
        onboardedAt: serverTimestamp(),
      }, { merge: true });

      // 2. Write userCatalogs entries — skip any already enrolled
      const existingSnap = await getDocs(
        query(collection(db, 'userCatalogs'), where('userId', '==', uid))
      );
      const alreadyEnrolled = new Set(
        existingSnap.docs.map(d => d.data().catalogId)
      );
      await Promise.all(
        selectedCatalogs
          .filter(catalogId => !alreadyEnrolled.has(catalogId))
          .map(catalogId =>
            addDoc(collection(db, 'userCatalogs'), {
              userId: uid,
              catalogId,
              enrolledAt: serverTimestamp(),
              enrolledBy: 'onboarding',
            })
          )
      );

      navigate('/learn/dashboard');
    } catch (e) {
      console.error('Error saving onboarding:', e);
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--color-gray-50, #f9fafb)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '3rem 1rem',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2.5rem', maxWidth: '560px' }}>
        <img
          src="/logo.png"
          alt="Litvill Lessons"
          style={{ width: '160px', marginBottom: '1rem' }}
          onError={e => { e.target.style.display = 'none'; }}
        />
        <h1 style={{
          fontSize: '1.75rem',
          fontWeight: 700,
          color: 'var(--color-teal, #0d9488)',
          marginBottom: '0.5rem',
        }}>
          Welcome to Litvill Lessons
        </h1>
        <p style={{ color: '#6b7280', margin: 0 }}>
          {step === 1
            ? 'What would you like to learn? Choose your area of focus.'
            : `Choose the courses you want to study in ${selectedTopic.title}.`}
        </p>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', alignItems: 'center' }}>
        {[1, 2].map(s => (
          <React.Fragment key={s}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8rem', fontWeight: 600,
              background: step >= s ? 'var(--color-teal, #0d9488)' : '#e5e7eb',
              color: step >= s ? 'white' : '#9ca3af',
              transition: 'all 0.2s',
            }}>
              {s}
            </div>
            {s < 2 && <div style={{ width: '40px', height: '2px', background: step > s ? 'var(--color-teal, #0d9488)' : '#e5e7eb' }} />}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div style={{
          background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca',
          borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1.5rem',
          fontSize: '0.875rem', maxWidth: '560px', width: '100%',
        }}>
          {error}
        </div>
      )}

      {/* Step 1 — Topic selection */}
      {step === 1 && (
        <div style={{
          display: 'flex', gap: '1rem', flexWrap: 'wrap',
          justifyContent: 'center', maxWidth: '760px',
        }}>
          {FIXED_TOPICS.map(topic => (
            <button
              key={topic.id}
              onClick={() => handleTopicSelect(topic)}
              style={{
                flex: '1 1 200px', maxWidth: '240px',
                background: 'white', border: '2px solid #e2e8f0',
                borderRadius: '12px', padding: '1.75rem 1.25rem',
                cursor: 'pointer', textAlign: 'center',
                transition: 'all 0.2s',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#0d9488';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(13,148,136,0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>{topic.icon}</div>
              <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.5rem', color: '#111827' }}>
                {topic.title}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.5 }}>
                {topic.description}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Step 2 — Course selection */}
      {step === 2 && (
        <div style={{ width: '100%', maxWidth: '560px' }}>

          {/* Back button */}
          <button
            onClick={() => { setStep(1); setSelectedTopic(null); setError(''); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-teal, #0d9488)', fontSize: '0.875rem',
              marginBottom: '1.25rem', padding: 0, display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            ← Back to topics
          </button>

          <div style={{
            background: 'white', borderRadius: '12px',
            border: '1px solid #e2e8f0', padding: '1.5rem',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <span style={{ fontSize: '1.5rem' }}>{selectedTopic.icon}</span>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{selectedTopic.title}</h2>
            </div>

            {loadingCatalogs ? (
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading courses…</p>
            ) : topicCatalogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem 0', color: '#6b7280' }}>
                <p style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🚧</p>
                <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>No courses available yet</p>
                <p style={{ fontSize: '0.8rem' }}>
                  This topic doesn&apos;t have any courses assigned. Please contact your coordinator.
                </p>
              </div>
            ) : (
              <>
                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
                  You can select one or more courses:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', marginBottom: '1.5rem' }}>
                  {topicCatalogs.map(catalog => {
                    const selected = selectedCatalogs.includes(catalog.id);
                    return (
                      <label key={catalog.id} style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '0.75rem 1rem',
                        border: `2px solid ${selected ? '#0d9488' : '#e2e8f0'}`,
                        borderRadius: '8px',
                        background: selected ? '#f0fdfa' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleCatalog(catalog.id)}
                          style={{ accentColor: '#0d9488', width: '18px', height: '18px', flexShrink: 0 }}
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.925rem', color: '#111827' }}>
                            {catalog.title}
                          </div>
                          {catalog.description && (
                            <div style={{ fontSize: '0.775rem', color: '#6b7280', marginTop: '2px' }}>
                              {catalog.description}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>

                <button
                  onClick={handleConfirm}
                  disabled={saving || selectedCatalogs.length === 0}
                  style={{
                    width: '100%', padding: '0.75rem',
                    background: selectedCatalogs.length === 0 ? '#d1d5db' : 'var(--color-teal, #0d9488)',
                    color: 'white', border: 'none', borderRadius: '8px',
                    fontSize: '0.95rem', fontWeight: 600,
                    cursor: selectedCatalogs.length === 0 || saving ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s',
                  }}
                >
                  {saving
                    ? 'Setting up your account…'
                    : selectedCatalogs.length === 0
                      ? 'Select at least one course'
                      : `Start Learning (${selectedCatalogs.length} course${selectedCatalogs.length !== 1 ? 's' : ''})`}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}